import type { LedgerKind, PracticeType } from '@packages/types';
import { getPracticeReports, type LedgerReportId } from '@packages/practice-config';

export const REPORT_TABS = [
  { id: 'income-statement', label: 'Income statement' },
  { id: 'balance-sheet', label: 'Balance sheet' },
  { id: 'trust-creditors', label: 'Trust reconciliation' },
  { id: 'trial-balance', label: 'Trial balance' },
  { id: 'cash-flow', label: 'Cash flow' },
  { id: 'cashbook', label: 'Cashbook' },
  { id: 'debtors-age-analysis', label: 'Debtors age analysis' },
  { id: 'creditors-age-analysis', label: 'Creditors age analysis' },
] as const;

export type ReportTabId = (typeof REPORT_TABS)[number]['id'];

export function isValidReportId(value: string | null): value is ReportTabId {
  return value !== null && REPORT_TABS.some((t) => t.id === value);
}

/**
 * Which report tabs are visible, decided by THREE independent axes - the trap
 * is conflating the last two, because an administered entity can carry
 * practice type BUSINESS just as a firm does.
 *
 * 1. **Ledger kind.** TRUST money is fiduciary, not the firm's own
 *    income/expenses, so the business reports (income statement, balance
 *    sheet, cash flow, and the two age analyses) are meaningless there and
 *    hidden; trial balance is common to both. TRUST additionally gets the
 *    three-section Trust reconciliation report (the `trust-creditors` id is
 *    an internal identifier only - it predates the display rename and stays
 *    for the route, service, and type names, per the backend-identifiers-vs-
 *    user-vocabulary convention), and - since `.filter()` preserves
 *    `REPORT_TABS`'s declaration order - it appears BEFORE trial balance for
 *    a TRUST ledger (the default/first tab), the report a trust practitioner
 *    needs first.
 * 2. **Practice type.** `@packages/practice-config`'s `reports` slice names
 *    the visible report ids per practice type: a going concern (a firm, or a
 *    company in business rescue trading on under its practitioner) sees
 *    every report; an entity that reports through a liquidation and
 *    distribution account instead of trading statements (INSOLVENCY,
 *    ESTATE_ADMINISTRATION) has the income statement, balance sheet and cash
 *    flow withheld, keeping trial balance and the two age analyses (the book
 *    debts and claims inherited at sequestration or death, not invoices the
 *    entity raised).
 * 3. **Administered-or-not.** The cashbook is the receipts-and-payments
 *    record for an administered entity's estate, so it follows the derived
 *    `administeredBy` signal, NOT the practice-type map - an administered
 *    entity may carry practice type BUSINESS as well as the three entity
 *    kinds (`PRACTICE_TYPE_CARRIERS` in `packages/types/src/api/account.ts`),
 *    and a BUSINESS-typed administered entity correctly keeps both the
 *    going-concern reports (it is run as a going concern) and the cashbook
 *    (it is administered). Folding the cashbook into the practice-type map
 *    would silently take it off that entity. A firm keeping its own books
 *    has no cashbook equivalent.
 */
export function visibleReportTabs(
  ledgerKind: LedgerKind,
  visiblePracticeReportIds: readonly LedgerReportId[],
  cashbook: boolean,
): (typeof REPORT_TABS)[number][] {
  if (ledgerKind === 'TRUST') {
    return REPORT_TABS.filter((t) => t.id === 'trial-balance' || t.id === 'trust-creditors');
  }
  return REPORT_TABS.filter((t) => {
    const id = t.id;
    if (id === 'trust-creditors') return false;
    if (id === 'cashbook') return cashbook;
    return visiblePracticeReportIds.includes(id);
  });
}

/**
 * The report strip for the signed-in account: resolves the two account-derived
 * inputs of `visibleReportTabs` so `ReportsTab` and the omnibox read them the
 * same way.
 *
 * An in-flight account needs no explicit loading branch for the practice
 * type: `getPracticeReports` defaults an absent practice type to ATTORNEY's
 * full list, so a practice type that is still undefined resolves to every
 * report rather than reading as "no reports". Treating an in-flight account
 * as anything narrower would take reports off the strip and then put them
 * back for every practice type that has them, and would reset a
 * `?report=...` deep link to the first tab on the way past. Erring this way
 * costs a non-trading entity one frame of tabs it will not keep, and only for
 * a super-admin - `LedgerPage`'s access gate holds every other caller on a
 * loading state until the account has arrived.
 *
 * The cashbook is on an administered entity, not a practice type. Keep the
 * tab while the account is in flight so a `?report=cashbook` deep link is not
 * reset for the readers who actually have one. The cost is a frame of a tab a
 * firm will not keep, and only for a super-admin.
 */
export function visibleReportTabsForAccount({
  ledgerKind,
  practiceType,
  isAccountLoading,
  administered,
}: {
  ledgerKind: LedgerKind;
  practiceType: PracticeType | null | undefined;
  isAccountLoading: boolean;
  administered: boolean;
}): (typeof REPORT_TABS)[number][] {
  const visiblePracticeReportIds = getPracticeReports(practiceType).visibleReportIds;
  const cashbook = isAccountLoading || administered;
  return visibleReportTabs(ledgerKind, visiblePracticeReportIds, cashbook);
}
